// A job's working days, in the Scheduling page's visit details (Session BDM).
//
// ONE JOB, ONE REFERENCE, ONE INVOICE, worked over several days (Director, 16 October 2026). This panel sets the days (a start date and a
// number of working days, weekends and bank holidays skipped unless you say otherwise), extends a job that is running longer than hoped,
// shortens one that ran short, moves a single day, and says who is working each day. Every change is PREVIEWED first (the days it adds and
// removes, and the capacity and leave warnings for each person on each added day, which never block), then confirmed in a second step.
// A day with time recorded cannot be removed and the last day cannot be removed; the server says why in plain words and this shows it.
// Nothing here bills anything or sends anything.
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { JobDaysPlan, JobDaysView, OvertimeDecisionResult } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatTime, formatWeekdayShort } from "../../lib/format";

const btn = "min-h-11 rounded-ele border border-ele-purple-accent px-3 font-heading text-xs tracking-wide text-ele-body transition hover:bg-ele-inset disabled:opacity-50";
const field = "min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green";

// A London calendar day ("YYYY-MM-DD") in words, through the platform's one date formatter: "Thu 15 Oct 2026".
const dayWords = (ymd: string): string => `${formatWeekdayShort(`${ymd}T12:00:00Z`)} ${ymd.slice(0, 4)}`;

function sentence(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const m = err.body && typeof err.body.message === "string" ? err.body.message : null;
    if (m) return m;
  }
  return fallback;
}

type Action =
  | { kind: "set"; startDate: string; workingDays: number; includeWeekends: boolean; includeBankHolidays: boolean }
  | { kind: "extend"; count: number; includeWeekends: boolean; includeBankHolidays: boolean }
  | { kind: "shorten"; count: number }
  | { kind: "move"; from: string; to: string };

const wording = (a: Action): string =>
  a.kind === "set"
    ? `Run over ${a.workingDays} working day${a.workingDays === 1 ? "" : "s"} from ${dayWords(a.startDate)}`
    : a.kind === "extend"
      ? `Add ${a.count} working day${a.count === 1 ? "" : "s"} after the last`
      : a.kind === "shorten"
        ? `Take ${a.count} day${a.count === 1 ? "" : "s"} off the end`
        : `Move ${dayWords(a.from)} to ${dayWords(a.to)}`;

export default function JobDaysPanel({
  jobId,
  editable,
  engineers,
  onChanged,
  defaultOpen = false,
}: {
  jobId: string;
  editable: boolean;
  engineers: { id: string; name: string }[];
  onChanged: () => void;
  // Open (and loaded) at once: a card that is already one day of a multi-day job. A one-day job's visit details do not read the days
  // until the office asks to spread it, so opening an ordinary visit costs nothing.
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [view, setView] = useState<JobDaysView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ action: Action; plan: JobDaysPlan } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"none" | "set" | "extend" | "shorten" | "move">("none");
  const [startDate, setStartDate] = useState("");
  const [count, setCount] = useState(1);
  const [weekends, setWeekends] = useState(false);
  const [holidays, setHolidays] = useState(false);
  const [moveFrom, setMoveFrom] = useState("");
  const [moveTo, setMoveTo] = useState("");
  const [peopleDay, setPeopleDay] = useState<string | null>(null);
  const [peopleSel, setPeopleSel] = useState<string[]>([]);
  // Session OTA: the office's yes or no to overtime an engineer asked for. The preview (names the engineer and the day) is read first;
  // only the second press of the branded confirm writes.
  const [overtime, setOvertime] = useState<{ day: string; engineerId: string; decision: "approve" | "refuse"; preview: OvertimeDecisionResult } | null>(null);

  // What the last decision said beyond "done" (for example that no timesheet entry existed to change): shown, never silent.
  const [overtimeResult, setOvertimeResult] = useState<string | null>(null);

  async function checkOvertime(day: string, engineerId: string, decision: "approve" | "refuse") {
    setBusy(true);
    setError(null);
    setOvertimeResult(null);
    try {
      setOvertime({ day, engineerId, decision, preview: await api.schedule.days.overtime(jobId, day, { engineerId, decision }) });
    } catch (err) {
      setOvertime(null);
      setError(sentence(err, "That overtime could not be checked. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function decideOvertime() {
    if (!overtime) return;
    setBusy(true);
    setError(null);
    try {
      // The hours the preview showed go back with the decision: if they changed meanwhile the server refuses and nothing is decided.
      const done = await api.schedule.days.overtime(jobId, overtime.day, {
        engineerId: overtime.engineerId,
        decision: overtime.decision,
        preview: false,
        arrivedAt: overtime.preview.arrivedAt,
        leftAt: overtime.preview.leftAt,
      });
      setOvertime(null);
      setOvertimeResult(done.note ?? null);
      await load();
      onChanged();
    } catch (err) {
      setError(sentence(err, "That overtime could not be decided. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const load = useCallback(async () => {
    try {
      setView(await api.schedule.days.get(jobId));
      setLoadError(null);
    } catch (err) {
      setLoadError(sentence(err, "The days could not be loaded."));
    }
  }, [jobId]);
  useEffect(() => {
    if (open) void load();
  }, [load, open]);

  async function run(a: Action, preview: boolean) {
    switch (a.kind) {
      case "set":
        return api.schedule.days.set(jobId, { startDate: a.startDate, workingDays: a.workingDays, includeWeekends: a.includeWeekends, includeBankHolidays: a.includeBankHolidays, preview });
      case "extend":
        return api.schedule.days.extend(jobId, { count: a.count, includeWeekends: a.includeWeekends, includeBankHolidays: a.includeBankHolidays, preview });
      case "shorten":
        return api.schedule.days.shorten(jobId, { count: a.count, preview });
      case "move":
        return api.schedule.days.move(jobId, { from: a.from, to: a.to, preview });
    }
  }

  async function check(a: Action) {
    setBusy(true);
    setError(null);
    try {
      const res = await run(a, true);
      setPending({ action: a, plan: res.plan });
    } catch (err) {
      setPending(null);
      setError(sentence(err, "That change could not be checked. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await run(pending.action, false);
      setPending(null);
      setMode("none");
      onChanged();
    } catch (err) {
      setError(sentence(err, "That change could not be made. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function savePeople(day: string) {
    setBusy(true);
    setError(null);
    try {
      await api.schedule.days.people(jobId, day, { engineerIds: peopleSel });
      setPeopleDay(null);
      await load();
      onChanged();
    } catch (err) {
      setError(sentence(err, "Who is working that day could not be changed."));
    } finally {
      setBusy(false);
    }
  }

  const days = view?.days ?? [];
  const multi = view?.multiDay === true;
  const everyone = new Map<string, string>();
  for (const d of days) for (const p of [...d.engineers, ...d.excluded]) everyone.set(p.engineerId, p.name ?? engineers.find((e) => e.id === p.engineerId)?.name ?? "Engineer");

  if (!open) {
    return (
      <div className="mt-3 border-t border-ele-purple-accent pt-3">
        <button type="button" className={btn} onClick={() => setOpen(true)}>
          Working days
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-ele-purple-accent pt-3" data-testid="job-days-panel">
      <h3 className="font-heading text-sm text-ele-strong">Working days</h3>
      {loadError && <p className="mt-1 text-xs text-ele-body">{loadError}</p>}
      {view && !multi && (
        <p className="mt-1 text-xs text-ele-body">
          This job runs on {days[0] ? dayWords(days[0].day) : "its booked day"} only. It can be spread over several working days: it stays ONE job with one reference and one invoice.
        </p>
      )}
      {view && multi && (
        <>
          <p className="mt-1 text-xs text-ele-body">One job over {days.length} days: one reference, one invoice. The lead engineer and crew are on every day unless you say otherwise.</p>
          <ul className="mt-2 space-y-1.5" aria-label="Working days">
            {days.map((d) => {
              const recorded = d.recorded.some((r) => r.arrivedAt || r.leftAt);
              return (
                <li key={d.day} className="rounded-ele border border-ele-card-border px-2 py-1.5 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-ele-strong">
                      {d.label} · {dayWords(d.day)}
                    </span>
                    {recorded && <span className="text-ele-body">Time recorded</span>}
                  </div>
                  <div className="text-ele-body">
                    {d.engineers.length > 0 ? d.engineers.map((p) => p.name ?? "Engineer").join(", ") : "Nobody named yet"}
                    {d.excluded.length > 0 && ` · off: ${d.excluded.map((p) => p.name ?? "Engineer").join(", ")}`}
                  </div>
                  {d.recorded
                    .filter((r) => r.overtimeBooked)
                    .map((r) => {
                      const who = r.name ?? "Engineer";
                      const approved = r.overtimeApproved === true;
                      const shown = overtime && overtime.day === d.day && overtime.engineerId === r.engineerId ? overtime : null;
                      return (
                        <div key={r.engineerId} className="mt-1 rounded-ele bg-ele-inset px-2 py-1.5" data-testid="job-days-overtime">
                          <p className="text-ele-strong">
                            {approved ? `Overtime approved by ${r.overtimeApprovedByName ?? "the office"}` : "Overtime asked for"}: {who}
                            {r.leftAt ? `, left ${formatTime(r.leftAt)}` : ""}
                          </p>
                          {!shown && (
                            <div className="mt-1 flex flex-wrap gap-2">
                              {!approved && (
                                <button type="button" className={btn} disabled={busy} onClick={() => void checkOvertime(d.day, r.engineerId, "approve")}>
                                  Approve overtime
                                </button>
                              )}
                              <button type="button" className={btn} disabled={busy} onClick={() => void checkOvertime(d.day, r.engineerId, "refuse")}>
                                {approved ? "Take the approval away" : "Refuse overtime"}
                              </button>
                            </div>
                          )}
                          {shown && (
                            <div className="mt-1 space-y-1" data-testid="job-days-overtime-preview">
                              <p className="text-ele-strong">{shown.preview.message}</p>
                              {shown.preview.minutesPastThreshold != null && shown.preview.minutesPastThreshold > 0 && (
                                <p className="text-ele-body">{Math.floor(shown.preview.minutesPastThreshold / 60)} h {shown.preview.minutesPastThreshold % 60} min past the rate card&apos;s daily threshold.</p>
                              )}
                              {shown.preview.willRewriteTimesheetEntry && <p className="text-ele-body">The job is already approved, so that day&apos;s timesheet entry is updated too.</p>}
                              {shown.preview.timesheetEntryMissing && shown.preview.note && <p className="text-ele-body">{shown.preview.note}</p>}
                              {shown.preview.payNote && <p className="text-ele-body">{shown.preview.payNote}</p>}
                              <div className="flex gap-2">
                                <ConfirmButton
                                  onConfirm={() => void decideOvertime()}
                                  label={shown.decision === "approve" ? `Approve ${who}'s overtime` : `Refuse ${who}'s overtime`}
                                  busy={busy}
                                  busyLabel="Saving..."
                                  className={btn}
                                />
                                <button type="button" className={btn} onClick={() => setOvertime(null)}>
                                  Leave it as it is
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {editable && everyone.size > 1 && (
                    <div className="mt-1">
                      {peopleDay === d.day ? (
                        <div className="space-y-1">
                          {[...everyone].map(([id, name]) => (
                            <label key={id} className="flex min-h-11 items-center gap-2 text-ele-strong">
                              <input type="checkbox" checked={peopleSel.includes(id)} onChange={(e) => setPeopleSel((s) => (e.target.checked ? [...s, id] : s.filter((x) => x !== id)))} />
                              {name}
                            </label>
                          ))}
                          <div className="flex gap-2">
                            <button type="button" className={btn} disabled={busy || peopleSel.length === 0} onClick={() => void savePeople(d.day)}>
                              Save who is working
                            </button>
                            <button type="button" className={btn} onClick={() => setPeopleDay(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={btn}
                          onClick={() => {
                            setPeopleDay(d.day);
                            setPeopleSel(d.engineers.map((p) => p.engineerId));
                          }}
                        >
                          Who is working
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {editable && view && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className={btn} onClick={() => setMode(mode === "set" ? "none" : "set")}>
            {multi ? "Reset the days" : "Run over several days"}
          </button>
          {multi && (
            <>
              <button type="button" className={btn} onClick={() => setMode(mode === "extend" ? "none" : "extend")}>
                Extend
              </button>
              <button type="button" className={btn} onClick={() => setMode(mode === "shorten" ? "none" : "shorten")}>
                Shorten
              </button>
              <button type="button" className={btn} onClick={() => setMode(mode === "move" ? "none" : "move")}>
                Move a day
              </button>
            </>
          )}
        </div>
      )}

      {editable && mode !== "none" && (
        <div className="mt-2 space-y-2 text-xs">
          {mode === "set" && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="grid gap-1 text-ele-body">
                First day
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={field} />
              </label>
              <label className="grid gap-1 text-ele-body">
                Working days
                <input type="number" min={1} max={90} value={count} onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))} className={`${field} w-24`} />
              </label>
            </div>
          )}
          {(mode === "extend" || mode === "shorten") && (
            <label className="grid gap-1 text-ele-body">
              {mode === "extend" ? "How many more working days" : "How many days off the end"}
              <input type="number" min={1} max={90} value={count} onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))} className={`${field} w-24`} />
            </label>
          )}
          {mode === "move" && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="grid gap-1 text-ele-body">
                Move this day
                <select value={moveFrom} onChange={(e) => setMoveFrom(e.target.value)} className={field}>
                  <option value="">Choose a day</option>
                  {days.map((d) => (
                    <option key={d.day} value={d.day}>
                      {d.label} · {dayWords(d.day)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-ele-body">
                To
                <input type="date" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className={field} />
              </label>
            </div>
          )}
          {(mode === "set" || mode === "extend") && (
            <div className="space-y-1 text-ele-body">
              <label className="flex min-h-11 items-center gap-2">
                <input type="checkbox" checked={weekends} onChange={(e) => setWeekends(e.target.checked)} /> Include Saturdays and Sundays
              </label>
              <label className="flex min-h-11 items-center gap-2">
                <input type="checkbox" checked={holidays} onChange={(e) => setHolidays(e.target.checked)} /> Include bank holidays
              </label>
            </div>
          )}
          <button
            type="button"
            className={btn}
            disabled={busy || (mode === "set" && !startDate) || (mode === "move" && (!moveFrom || !moveTo))}
            onClick={() =>
              void check(
                mode === "set"
                  ? { kind: "set", startDate, workingDays: count, includeWeekends: weekends, includeBankHolidays: holidays }
                  : mode === "extend"
                    ? { kind: "extend", count, includeWeekends: weekends, includeBankHolidays: holidays }
                    : mode === "shorten"
                      ? { kind: "shorten", count }
                      : { kind: "move", from: moveFrom, to: moveTo },
              )
            }
          >
            Check this change
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-ele-strong">
          {error}
        </p>
      )}
      {overtimeResult && (
        <p role="status" className="mt-2 text-xs text-ele-strong" data-testid="job-days-overtime-result">
          {overtimeResult}
        </p>
      )}

      {pending && (
        <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-inset p-2 text-xs" data-testid="job-days-preview">
          <p className="text-ele-strong">{wording(pending.action)}.</p>
          {pending.plan.added.length > 0 && <p className="text-ele-body">Adds: {pending.plan.added.map(dayWords).join("; ")}.</p>}
          {pending.plan.removed.length > 0 && <p className="text-ele-body">Removes: {pending.plan.removed.map(dayWords).join("; ")}.</p>}
          {pending.plan.warnings.capacityWarning && <p className="mt-1 text-ele-body">{pending.plan.warnings.capacityWarning}</p>}
          {pending.plan.warnings.leaveWarning && <p className="mt-1 text-ele-body">{pending.plan.warnings.leaveWarning}</p>}
          <p className="mt-1 text-ele-body">The same job, the same reference and the same invoice. The engineers see the change on their phones and calendars.</p>
          {pending.plan.billingNote && (
            <p className="mt-1 text-ele-body" data-testid="job-days-billing-note">
              {pending.plan.billingNote}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <ConfirmButton onConfirm={() => void confirm()} label="Make this change" busy={busy} busyLabel="Making the change..." className={btn} />
            <button type="button" className={btn} onClick={() => setPending(null)}>
              Leave it as it is
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
